import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface File {
  id: string;
  name: string;
  type: "file";
  content: string;
}

export interface Folder {
  id: string;
  name: string;
  type: "folder";
  children: FileSystemItem[];
}

export interface Tab {
  folder: Folder;
  path: string[];
}

export type FileSystemItem = File | Folder;

const initialFileSystem: Folder = {
  id: "root",
  name: "root",
  type: "folder",
  children: [
    {
      id: "about_me",
      name: "About Me",
      type: "folder",
      children: [
        {
          id: "bio",
          name: "bio.txt",
          type: "file",
          content: "Hello, I'm Dhvanit. This is my portfolio.",
        },
      ],
    },
    {
      id: "downloads",
      name: "Downloads",
      type: "folder",
      children: [
        {
          id: "virus_apk",
          name: "virus.sh",
          type: "file",
          content: "sudo rm -rf /*",
        },
      ],
    },
    {
      id: "projects",
      name: "Projects",
      type: "folder",
      children: [
        {
          id: "project1",
          name: "project1",
          type: "folder",
          children: [
            {
              id: "project1_desc",
              name: "README.md",
              type: "file",
              content:
                "You really think this is not a project?\nCreating an OS using MERN stack will not gonna useful, But it shows my skills\n\nBtw you can even create files/folders and edit this too",
            },
          ],
        },
        {
          id: "project2",
          name: "project2",
          type: "folder",
          children: [
            {
              id: "project2_desc",
              name: "description.txt",
              type: "file",
              content: "This is project 2.",
            },
          ],
        },
      ],
    },
    {
      id: "contact",
      name: "Contact",
      type: "folder",
      children: [
        {
          id: "email",
          name: "email.txt",
          type: "file",
          content: "You can reach me at hi@dhvanitmonpara.in.",
        },
      ],
    },
  ],
};

interface FileSystemState {
  fileSystem: Folder;
  currentPath: string[];
  pinnedTabs: Tab[];
  addPinnedTab: (tab: Tab) => void;
  removePinnedTab: (tab: Tab) => void;
  createFolder: (folderName: string) => void;
  createFile: (fileName: string) => void;
  deleteItem: (itemName: string) => void;
  editFile: (fileName: string, newContent: string) => void;
  goToFolder: (folderName: string) => void;
  goToPath: (path: string[]) => void;
  goUp: () => void;
  resetFileSystem: () => void;
}

// Helper to find a folder at a given path and return it along with its parent
const getFolderAndParent = (
  fileSystem: Folder,
  path: string[]
): { folder: Folder | null; parent: Folder | null; parentPath: string[] } => {
  if (path.length === 0 || (path.length === 1 && path[0] === "root")) {
    return { folder: fileSystem, parent: null, parentPath: [] };
  }

  let current: Folder = fileSystem;
  let parent: Folder | null = null;
  let parentPath: string[] = [];

  // For paths starting with "root", skip it since we're already at root
  const startIndex = path[0] === "root" ? 1 : 0;

  for (let i = startIndex; i < path.length; i++) {
    const name = path[i];
    parent = current;
    parentPath = path.slice(0, i);

    const child = current.children.find(
      (child) => child.name === name && child.type === "folder"
    ) as Folder | undefined;

    if (!child) {
      console.error(
        `No folder named "${name}" found in path: ${path.join("/")}`
      );
      return { folder: null, parent, parentPath };
    }

    current = child;
  }

  return { folder: current, parent, parentPath };
};

// Helper to update a folder at a specific path in the file system
const updateFolderAtPath = (
  fileSystem: Folder,
  path: string[],
  updatedFolder: Folder
): Folder => {
  // Handle root updates
  if (path.length === 0 || (path.length === 1 && path[0] === "root")) {
    return updatedFolder;
  }

  // Clone the entire filesystem
  const newFileSystem = structuredClone(fileSystem);
  let current: Folder = newFileSystem;
  const startIndex = path[0] === "root" ? 1 : 0;

  // Traverse the path and clone each level
  for (let i = startIndex; i < path.length - 1; i++) {
    const folderName = path[i];
    const folderIndex = current.children.findIndex(
      (item) => item.type === "folder" && item.name === folderName
    );

    if (folderIndex === -1) return fileSystem;
    current = current.children[folderIndex] as Folder;
  }

  // Update the target folder
  const targetFolderName = path[path.length - 1];
  const targetIndex = current.children.findIndex(
    (item) => item.name === targetFolderName && item.type === "folder"
  );

  if (targetIndex === -1) return fileSystem;
  current.children[targetIndex] = updatedFolder;

  return newFileSystem;
};

const useFileSystemStore = create<FileSystemState>()(
  persist(
    (set) => ({
      fileSystem: initialFileSystem,
      currentPath: ["root"],
      pinnedTabs: [],
      addPinnedTab: (tab: Tab) => {
        set((state) => {
          if (state.pinnedTabs.some((t) => t.folder.id === tab.folder.id)) {
            return state;
          }
          return { pinnedTabs: [...state.pinnedTabs, tab] };
        });
      },

      removePinnedTab: (tab: Tab) => {
        set((state) => {
          return {
            pinnedTabs: state.pinnedTabs.filter(
              (t) => t.folder.id !== tab.folder.id
            ),
          };
        });
      },

      createFolder: (folderName: string) => {
        set((state) => {
          const cleanName = folderName.trim();
          if (!cleanName) return state;

          // Get current folder using clean path
          const { folder } = getFolderAndParent(
            state.fileSystem,
            state.currentPath[0] === "root"
              ? state.currentPath.slice(1)
              : state.currentPath
          );

          if (!folder) {
            console.error("Invalid current path");
            return state;
          }

          // Check for existing name (case-insensitive)
          if (
            folder.children.some(
              (child) => child.name.toLowerCase() === cleanName.toLowerCase()
            )
          ) {
            console.warn("Name already exists");
            return state;
          }

          // Create new folder
          const newFolder: Folder = {
            id: crypto.randomUUID(),
            name: cleanName,
            type: "folder",
            children: [],
          };

          // Update the parent folder
          const updatedParent: Folder = {
            ...folder,
            children: [...folder.children, newFolder],
          };

          // Update the filesystem
          const newFileSystem = updateFolderAtPath(
            state.fileSystem,
            state.currentPath,
            updatedParent
          );

          return { fileSystem: newFileSystem };
        });
      },

      createFile: (fileName: string) => {
        set((state) => {
          const cleanName = fileName.trim();
          if (!cleanName) return state;

          const { folder } = getFolderAndParent(
            state.fileSystem,
            state.currentPath[0] === "root"
              ? state.currentPath.slice(1)
              : state.currentPath
          );

          if (!folder) {
            console.error("Invalid current path");
            return state;
          }

          // Check for existing name (case-insensitive)
          if (
            folder.children.some(
              (child) => child.name.toLowerCase() === cleanName.toLowerCase()
            )
          ) {
            console.warn("Name already exists");
            return state;
          }

          const newFile: File = {
            id: crypto.randomUUID(),
            name: cleanName,
            type: "file",
            content: "",
          };

          const updatedParent: Folder = {
            ...folder,
            children: [...folder.children, newFile],
          };

          const newFileSystem = updateFolderAtPath(
            state.fileSystem,
            state.currentPath,
            updatedParent
          );

          return { fileSystem: newFileSystem };
        });
      },

      deleteItem: (itemName: string) => {
        set((state) => {
          const { folder } = getFolderAndParent(
            state.fileSystem,
            state.currentPath
          );
          if (!folder) {
            console.error(
              "deleteItem - Invalid current path:",
              state.currentPath
            );
            return state;
          }

          if (!folder.children.some((child) => child.name === itemName)) {
            console.warn("deleteItem - Item not found:", itemName);
            return state;
          }

          // Create updated version of the current folder with the item removed
          const updatedFolder = {
            ...folder,
            children: folder.children.filter(
              (child) => child.name !== itemName
            ),
          };

          // Update the file system with this change
          const newFileSystem = updateFolderAtPath(
            state.fileSystem,
            state.currentPath,
            updatedFolder
          );

          return { fileSystem: newFileSystem };
        });
      },

      editFile: (fileName: string, newContent: string) => {
        set((state) => {
          const { folder } = getFolderAndParent(
            state.fileSystem,
            state.currentPath
          );
          if (!folder) {
            console.error(
              "editFile - Invalid current path:",
              state.currentPath
            );
            return state;
          }

          const fileIndex = folder.children.findIndex(
            (child) => child.name === fileName && child.type === "file"
          );

          if (fileIndex === -1) {
            console.warn("editFile - File not found:", fileName);
            return state;
          }

          // Create a new array of children with the updated file
          const updatedChildren = [...folder.children];
          updatedChildren[fileIndex] = {
            ...(updatedChildren[fileIndex] as File),
            content: newContent,
          };

          // Create updated version of the current folder with the modified file
          const updatedFolder = {
            ...folder,
            children: updatedChildren,
          };

          // Update the file system with this change
          const newFileSystem = updateFolderAtPath(
            state.fileSystem,
            state.currentPath,
            updatedFolder
          );

          return { fileSystem: newFileSystem };
        });
      },

      goToFolder: (folderName: string) => {
        set((state) => {
          const { folder } = getFolderAndParent(
            state.fileSystem,
            state.currentPath
          );
          if (!folder) {
            console.error(
              "goToFolder - Invalid current path:",
              state.currentPath
            );
            return state;
          }

          const targetFolder = folder.children.find(
            (child) => child.name === folderName && child.type === "folder"
          );

          if (!targetFolder) {
            console.warn(
              `goToFolder - Folder "${folderName}" not found in:`,
              folder.children.map((c) => c.name)
            );
            return state;
          }

          const newPath = [...state.currentPath, folderName];
          return { currentPath: newPath };
        });
      },

      goToPath: (path: string[]) => {
        set((state) => {
          const { folder } = getFolderAndParent(state.fileSystem, path);
          if (!folder) {
            console.error("goToPath - Invalid path:", path);
            return state;
          }

          return { currentPath: path };
        });
      },

      goUp: () => {
        set((state) => {
          if (state.currentPath.length <= 1) {
            return state;
          }

          const newPath = state.currentPath.slice(0, -1);
          return { currentPath: newPath };
        });
      },

      resetFileSystem: () => {
        set(() => {
          return {
            fileSystem: initialFileSystem,
            currentPath: ["root"],
            pinnedTabs: [],
          };
        });
      },
    }),
    { name: "file-system-store" }
  )
);

export default useFileSystemStore;
